import React from 'react';
import { bool, node, number, oneOf, oneOfType, string } from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { border, color, sizing, spacing } from '@/components/styled-system';
import { getRowAlign } from './utils';

const DEFAULT_GUTTERS = 16;

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 0;
  margin-bottom: 0;

  ${({ wrap }) => css`
    flex-wrap: ${wrap};
  `}

  ${({ noGutters, gutters, align }) => css`
    margin-left: ${noGutters ? 0 : `-${gutters || DEFAULT_GUTTERS}px`};
    margin-right: ${noGutters ? 0 : `-${gutters || DEFAULT_GUTTERS}px`};

    ${getRowAlign(align)};

    > div {
      padding-left: ${noGutters ? 0 : `${gutters || DEFAULT_GUTTERS}px`};
      padding-right: ${noGutters ? 0 : `${gutters || DEFAULT_GUTTERS}px`};
    }
  `}

  ${spacing};
  ${sizing};
  ${color};
  ${border};
`;

/**
 * Row Component
 * @typedef {{
 * children: React.ReactNode,
 * gutters: number,
 * noGutters: boolean,
 * align: string }
 * & import('../../styled-system').BorderProps
 * & import('../../styled-system').ColorProps
 * & import('../../styled-system').SizingProps
 * & import('../../styled-system').SpacingProps} RowProps
 * @type {React.FC<RowProps>}
 * @returns
 */
const Row = ({ children, ...props }) => <StyledRow {...props}>{children}</StyledRow>;

Row.propTypes = {
  children: oneOfType([string, node]),
  align: string,
  noGutters: bool,
  gutters: number,
  wrap: oneOf(['wrap', 'nowrap']),
};

Row.defaultProps = {
  children: null,
  align: 'left',
  noGutters: false,
  gutters: null,
  wrap: 'nowrap',
};

export default Row;
